// ============================================================
// Die Abfrageschlüssel von TanStack Query — ZENTRAL (SPEC §13.4).
//
// Warum an einer Stelle und nicht je Bereich: der Ereignisstrom (api/sse.ts)
// entwertet bereichsübergreifend. Ein `karte`-Ereignis betrifft den Katalog
// (P5), den Seitenplan (P1/P5) und womöglich das Modell (P6); stünden die
// Schlüssel in drei Hook-Dateien, müsste sse.ts alle drei kennen — und der
// vierte Bereich, der dazukommt, wäre der, den es vergisst.
//
// Die Formen aus §4.2 stehen wörtlich (`['katalog', id]`, `['karte', id]`,
// `['seitenplan', id, modus]`, `['anwesenheit', id]`, `['bilder', katalogId]`).
// Alles Weitere folgt einer Regel: das erste Glied ist der Bereich, das zweite die
// Kennung, danach die Unterabfrage — so trifft `invalidateQueries({queryKey:
// ['import', id]})` auch Baum, Tabelle und Typgruppen dieses Imports (Präfix).
//
// Die Pakete benutzen GENAU diese Fabriken; wer einen neuen Schlüssel braucht,
// bittet in docs/INTEGRATION.md darum (die Datei gehört P0 bzw. dem Integrator).
// ============================================================
import type { Modus } from "./types";

/** Filter einer Liste als Schlüsselglied (TanStack vergleicht Objekte tief). */
type Filter = Record<string, string | number | boolean | null | undefined>;

export const schluessel = {
  // ---- P0 ---------------------------------------------------------------------
  ich: ["ich"] as const,
  konfig: ["konfig"] as const,
  authConfig: ["auth-config"] as const,
  nutzer: ["admin", "nutzer"] as const,

  // ---- P2/P5 — Projekte, Kataloge, Editor (§4.2, §4.11, §3.6) --------------------
  projekte: ["projekte"] as const,
  projekt: (id: string) => ["projekt", id] as const,
  projekteSuche: (q: string) => ["projekte", "suche", q] as const,
  kataloge: ["kataloge"] as const,
  geloeschteKataloge: ["kataloge", "geloescht"] as const,
  katalog: (id: string) => ["katalog", id] as const,
  karte: (id: string) => ["karte", id] as const,
  kartenFassungen: (id: string) => ["karte", id, "fassungen"] as const,
  kartenFassung: (id: string, version: number) => ["karte", id, "fassungen", version] as const,
  herkunftVergleich: (karteId: string) => ["karte", karteId, "herkunft"] as const,
  /** §9.7: Elemente und Mengen einer Karte aus dem Modell. */
  karteModell: (karteId: string) => ["karte", karteId, "modell"] as const,
  /** §19.16: Aufbau-Vorschlag einer Karte. UNTER `karteModell`: das Ereignis `zuordnung`
   *  entwertet alle Modellabfragen der Karten (sse.ts, drittes Glied „modell") — eine neue
   *  Zuordnung ändert den Vorschlag genauso wie die Menge. */
  karteAufbau: (karteId: string) => ["karte", karteId, "modell", "aufbau"] as const,
  seitenplan: (katalogId: string, modus: Modus) => ["seitenplan", katalogId, modus] as const,
  /** Präfix beider Modi — zum Entwerten. */
  seitenplaene: (katalogId: string) => ["seitenplan", katalogId] as const,
  anwesenheit: (katalogId: string) => ["anwesenheit", katalogId] as const,
  bilder: (katalogId: string) => ["bilder", katalogId] as const,
  /** Präfix ALLER Bildlisten (Katalog- und Produktbilder tragen dieselbe Zeile, `Bild`
   *  nennt weder Katalog noch Produkt) — zum Ersetzen einer geänderten Zeile (P5-13). */
  alleBilder: ["bilder"] as const,
  /** Präfix aller Seitenpläne aller Kataloge (P5-13). */
  alleSeitenplaene: ["seitenplan"] as const,
  /** Setzfehler je Seite (§4.1, P5-3/P5-14): `{[nr]: {hash, detail}}`, nur aus dem Ereignis
   *  `seite_fehler` gefüllt (sse.ts), nie vom Server geholt. Bewusst NICHT unter
   *  `["seitenplan", …]`: das Entwerten des Plans risse ihn sonst mit. */
  seitenFehler: (katalogId: string, modus: Modus) => ["seitenfehler", katalogId, modus] as const,

  // ---- P2/P5 — Stände, Exporte (§6, §5.7) ------------------------------------------
  pruefung: (katalogId: string) => ["katalog", katalogId, "pruefung"] as const,
  staende: (katalogId: string) => ["staende", katalogId] as const,
  stand: (id: string) => ["stand", id] as const,
  standVergleich: (id: string, mit: string) => ["stand", id, "vergleich", mit] as const,
  exporte: (katalogId: string) => ["exporte", katalogId] as const,
  export: (id: string) => ["export", id] as const,

  // ---- P3/P6 — Import, Modell, Zuordnen (§8, §9) -----------------------------------
  importe: (projektId: string) => ["projekt", projektId, "importe"] as const,
  zuordnungen: (projektId: string) => ["projekt", projektId, "zuordnungen"] as const,
  import: (id: string) => ["import", id] as const,
  importSchema: (id: string) => ["import", id, "schema"] as const,
  baum: (id: string, gliederung: string) => ["import", id, "baum", gliederung] as const,
  baumKnoten: (id: string, gliederung: string, knoten: string) => ["import", id, "baum", gliederung, knoten] as const,
  elemente: (id: string, filter: Filter) => ["import", id, "elemente", filter] as const,
  element: (id: string, guid: string) => ["import", id, "element", guid] as const,
  werte: (id: string, pfad: string, filter: Filter) => ["import", id, "werte", pfad, filter] as const,
  /** Ohne Katalog das Präfix beider Formen; mit Katalog (auch `null` = „ohne Katalog")
   *  der Schlüssel von `GET /importe/{id}/zustand?katalog=` (§9.8, P6-2). Das dritte Glied
   *  bleibt `zustand`, so trifft das Ereignis `zuordnung` beide. */
  zustand: (id: string, katalogId?: string | null) =>
    katalogId === undefined ? (["import", id, "zustand"] as const) : (["import", id, "zustand", katalogId ?? ""] as const),
  typgruppen: (id: string, filter: Filter) => ["import", id, "typgruppen", filter] as const,
  /** Präfix aller Modellabfragen eines Imports (Baum, Tabelle, Zustand, Typgruppen). */
  modell: (importId: string) => ["import", importId] as const,

  // ---- P2/P7 — Bibliothek, Kategorien, Regeln (§7) ----------------------------------
  bibliothek: ["bibliothek"] as const,
  bibliothekListe: (filter: Filter) => ["bibliothek", "liste", filter] as const,
  produkt: (id: string) => ["bibliothek", id] as const,
  produktFassungen: (id: string) => ["bibliothek", id, "fassungen"] as const,
  /** `GET /bibliothek/schluessel?kategorie=` (§4.3, §7.6; P5-12): Feldschlüssel als Vorschläge.
   *  Das zweite Glied ist kein uuid, kollidiert also nicht mit `produkt(id)`. */
  bibliothekSchluessel: (kategorieId?: string | null) => ["bibliothek", "schluessel", kategorieId ?? ""] as const,
  kategorien: ["kategorien"] as const,
  regeln: ["regeln"] as const,
  regelnListe: (filter: Filter) => ["regeln", filter] as const,

  // ---- P4/P7 — Anreicherung, KI (§10, §11) ------------------------------------------
  anreicherungen: ["anreicherung"] as const,
  anreicherungenListe: (filter: Filter) => ["anreicherung", "liste", filter] as const,
  anreicherung: (id: string) => ["anreicherung", id] as const,
  ki: ["ki"] as const,
  kiStatus: ["ki", "status"] as const,
  kiSitzungen: ["ki", "sitzungen"] as const,
  kiSitzung: (id: string) => ["ki", "sitzungen", id] as const,
  /** Die Liste mit Filter: ein Objekt als drittes Glied trifft nie `kiSitzung(id)` (P7-6). */
  kiSitzungenListe: (filter: Filter) => ["ki", "sitzungen", filter] as const,

  // ---- P2/P7 — VW-Schnittstelle (§16) ------------------------------------------------
  ernte: ["ernte"] as const,
  ernteListe: (filter: Filter) => ["ernte", filter] as const,
  maschinenToken: ["admin", "maschinen-token"] as const,
};
